import { normalizeTier, type PlanTier } from '@/lib/planLimits';
import {
  ALL_CATALOG_KEYS,
  parseCatalogKey,
  envVarCandidates,
  type PlanProductKey,
} from '@/lib/stripe/price-catalog';

/** Historical prices remain recognizable so existing subscribers are never migrated implicitly. */
const HISTORICAL_PRICE_TIERS: Record<string, PlanTier> = {
  price_1TEzKoPCzRLjtVSpnotZEPYq: 'verified',
  price_1TJQfFPCzRLjtVSp046t2MRK: 'verified',
  price_1TJQYCPCzRLjtVSpvSwaBiyF: 'legacy',
  price_1TJUSLPCzRLjtVSpim7TpwLJ: 'legacy',
  price_1TJQaUPCzRLjtVSpC6Tz1LJO: 'institution_starter',
  price_1TJUStPCzRLjtVSphDxlocN7: 'institution_starter',
  price_1TJQajPCzRLjtVSpiiiDnilv: 'institution_growth',
  price_1TJUbjPCzRLjtVSpr3rISXTR: 'institution_growth',
  price_1TJUVwPCzRLjtVSpnd11gxrh: 'vaas',
};

function productToTier(product: string): PlanTier | null {
  if (product.startsWith('individual_verified')) return 'verified';
  if (product.startsWith('individual_legacy')) return 'legacy';
  if (product.startsWith('collective_verified')) return 'collective_verified';
  if (product.startsWith('collective_pro')) return 'collective_pro';
  if (product.startsWith('institution_starter')) return 'institution_starter';
  if (product.startsWith('institution_growth')) return 'institution_growth';
  if (product === 'vaas_one_time') return 'vaas';
  return null;
}

/** Map Stripe price IDs to internal plan tiers. Supports USD + CAD env-configured prices. */
export function mapPriceIdToTier(priceId: string): PlanTier | null {
  if (HISTORICAL_PRICE_TIERS[priceId]) return HISTORICAL_PRICE_TIERS[priceId];

  for (const catalogKey of ALL_CATALOG_KEYS) {
    const parsed = parseCatalogKey(catalogKey);
    if (!parsed) continue;
    const tier = productToTier(parsed.product);
    if (!tier) continue;
    for (const envName of envVarCandidates(parsed.product as PlanProductKey, parsed.currency)) {
      const envPrice = process.env[envName];
      if (envPrice && envPrice === priceId) return tier;
    }
  }

  return null;
}

export function tierFromStripeSubscriptionStatus(status: string, planTier: PlanTier): PlanTier {
  if (status === 'active' || status === 'trialing' || status === 'past_due') {
    return planTier;
  }
  return 'free';
}

export function isPaidStripeSubscriptionStatus(status: string | null | undefined): boolean {
  return status === 'active' || status === 'trialing' || status === 'past_due';
}

export { normalizeTier };
